/**
 * NOM035ConfigPage.js
 * Página completa para el wizard de configuración NOM-035
 * Se muestra en el menú de configuración del admin en lugar del dashboard
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Shield,
  Building,
  Users,
  FileText,
  Megaphone,
  AlertCircle,
  ClipboardList,
  Target,
  FileCheck,
  MessageSquare,
  Check,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Search,
  ChevronsUpDown
} from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from './ui/command';
import logger from '../utils/logger';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from './ui/popover';

// Step order and icons
const STEP_ORDER = ['DATOS_EMPRESA', 'RESPONSABLE', 'POLITICA', 'DIFUSION', 'GUIA_I', 'GUIA_II_III', 'PROGRAMA', 'EVIDENCIAS', 'COMUNICACION'];

const STEP_ICONS = {
  DATOS_EMPRESA: Building,
  RESPONSABLE: Users,
  POLITICA: FileText,
  DIFUSION: Megaphone,
  DIFUSION_POLITICA: Megaphone,
  GUIA_I: AlertCircle,
  GUIA_II_III: ClipboardList,
  PARTICIPACION: ClipboardList,
  PROGRAMA: Target,
  EVIDENCIAS: FileCheck,
  COMUNICACION: MessageSquare
};

// Texts for both languages
const TEXTS = {
  es: {
    title: 'Configuración NOM-035',
    subtitle: 'Asistente de configuración para el cumplimiento de la NOM-035-STPS-2018',
    steps: {
      DATOS_EMPRESA: 'Datos del centro de trabajo',
      RESPONSABLE: 'Responsable NOM-035',
      POLITICA: 'Política de prevención',
      DIFUSION: 'Difusión de la política',
      DIFUSION_POLITICA: 'Difusión de la política',
      GUIA_I: 'Guía I - ATS',
      GUIA_II_III: 'Guía II/III - Factores de riesgo',
      PARTICIPACION: 'Participación',
      PROGRAMA: 'Programa de atención',
      EVIDENCIAS: 'Evidencias',
      COMUNICACION: 'Comunicación de resultados'
    },
    save: 'Guardar',
    saving: 'Guardando...',
    next: 'Siguiente',
    back: 'Anterior',
    complete: 'Completar',
    step: 'Paso',
    of: 'de',
    workplaceName: 'Nombre del centro de trabajo',
    numberOfWorkers: 'Número de trabajadores',
    legalRepresentative: 'Representante legal',
    cycleStartDate: 'Fecha inicio ciclo',
    responsibleType: 'Tipo de responsable',
    internal: 'Interno',
    external: 'Externo',
    selectEmployee: 'Seleccionar empleado',
    searchEmployee: 'Buscar empleado...',
    noEmployeeFound: 'No se encontró empleado',
    externalName: 'Nombre del responsable externo',
    externalEmail: 'Email del responsable externo',
    position: 'Cargo',
    principlesDeclaration: 'Declaración de principios',
    specificCommitments: 'Compromisos específicos',
    reportingMechanism: 'Mecanismo de denuncia',
    diffusionMethods: 'Métodos de difusión',
    otherMethod: 'Otro método',
    describeMethod: 'Describe el método...',
    atsQuestion: '¿Se identificaron trabajadores con exposición a ATS?',
    yes: 'Sí',
    no: 'No',
    atsCount: 'Número de trabajadores con ATS',
    atsActions: 'Acciones tomadas',
    communicationMethods: 'Métodos de comunicación',
    describeCommunication: 'Describe el método de comunicación...',
    mainActivity: 'Actividad principal de la empresa',
    mainActivities: 'Principales actividades de los trabajadores',
    professionalLicense: 'Cédula profesional del responsable',
    nom030Label: 'Integración NOM-030',
    nom030Check: 'Confirmo que se ha verificado la relación con NOM-030-STPS',
    nom030Evidence: 'URL de evidencia (opcional)'
  },
  en: {
    title: 'NOM-035 Configuration',
    subtitle: 'Configuration wizard for NOM-035-STPS-2018 compliance',
    steps: {
      DATOS_EMPRESA: 'Workplace data',
      RESPONSABLE: 'NOM-035 Responsible',
      POLITICA: 'Prevention policy',
      DIFUSION: 'Policy diffusion',
      DIFUSION_POLITICA: 'Policy diffusion',
      GUIA_I: 'Guide I - STE',
      GUIA_II_III: 'Guide II/III - Risk factors',
      PARTICIPACION: 'Participation',
      PROGRAMA: 'Attention program',
      EVIDENCIAS: 'Evidence',
      COMUNICACION: 'Results communication'
    },
    save: 'Save',
    saving: 'Saving...',
    next: 'Next',
    back: 'Back',
    complete: 'Complete',
    step: 'Step',
    of: 'of',
    workplaceName: 'Workplace name',
    numberOfWorkers: 'Number of workers',
    legalRepresentative: 'Legal representative',
    cycleStartDate: 'Cycle start date',
    responsibleType: 'Responsible type',
    internal: 'Internal',
    external: 'External',
    selectEmployee: 'Select employee',
    searchEmployee: 'Search employee...',
    noEmployeeFound: 'No employee found',
    externalName: 'External responsible name',
    externalEmail: 'External responsible email',
    position: 'Position',
    principlesDeclaration: 'Principles declaration',
    specificCommitments: 'Specific commitments',
    reportingMechanism: 'Reporting mechanism',
    diffusionMethods: 'Diffusion methods',
    otherMethod: 'Other method',
    describeMethod: 'Describe the method...',
    atsQuestion: 'Were workers with STE exposure identified?',
    yes: 'Yes',
    no: 'No',
    atsCount: 'Number of workers with STE',
    atsActions: 'Actions taken',
    communicationMethods: 'Communication methods',
    describeCommunication: 'Describe the communication method...',
    mainActivity: 'Main activity of the company',
    mainActivities: 'Main activities performed by workers',
    professionalLicense: 'Professional license of the responsible',
    nom030Label: 'NOM-030 Integration',
    nom030Check: 'I confirm NOM-030-STPS relationship has been verified',
    nom030Evidence: 'Evidence URL (optional)'
  }
};

// Diffusion method options
const DIFFUSION_OPTIONS = [
  { id: 'email', label_es: 'Correo electrónico', label_en: 'Email' },
  { id: 'reunion', label_es: 'Reunión informativa', label_en: 'Informational meeting' },
  { id: 'intranet', label_es: 'Intranet / Portal interno', label_en: 'Intranet / Internal portal' },
  { id: 'cartel', label_es: 'Cartel o infografía', label_en: 'Poster or infographic' },
  { id: 'capacitacion', label_es: 'Sesión de capacitación', label_en: 'Training session' },
  { id: 'otro', label_es: 'Otro método', label_en: 'Other method' }
];

// Communication method options (same as diffusion for consistency)
const COMMUNICATION_OPTIONS = DIFFUSION_OPTIONS;

// Policy templates
const POLICY_TEMPLATES = {
  declaracion_principios: (empresa, representante) => 
    `${empresa}, representada por ${representante}, establece como política la prevención de los factores de riesgo psicosocial, la promoción de un entorno organizacional favorable y la atención de situaciones de violencia laboral, en cumplimiento con la NOM-035-STPS-2018.`,
  compromisos_especificos: (empresa, responsable, cargo) =>
    `${empresa} se compromete a:\n\n1. Identificar y analizar los factores de riesgo psicosocial.\n2. Evaluar el entorno organizacional.\n3. Adoptar medidas de prevención y control.\n4. Practicar exámenes médicos a trabajadores expuestos a violencia laboral.\n5. Difundir la información necesaria a los trabajadores.\n\nResponsable de implementación: ${responsable}${cargo ? ` (${cargo})` : ''}`,
  mecanismo_denuncia: (empresa, responsable, cargo) =>
    `${empresa} ha establecido los siguientes mecanismos seguros y confidenciales para la recepción de quejas:\n\n• Buzón electrónico confidencial\n• Línea de atención directa con ${responsable}${cargo ? ` (${cargo})` : ''}\n• Formato de queja disponible en recursos humanos\n\nTodas las denuncias serán tratadas de forma confidencial y sin represalias.`
};

const NOM035ConfigPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const texts = TEXTS[language] || TEXTS.es;
  
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre_centro_trabajo: '',
    numero_trabajadores: '',
    nombre_representante_legal: '',
    rfc_centro_trabajo: '',
    fecha_inicio_ciclo: '',
    responsable_tipo: 'interno',
    responsable_user_id: '',
    responsable_externo_nombre: '',
    responsable_externo_email: '',
    cargo_responsable: '',
    declaracion_principios: '',
    compromisos_especificos: '',
    mecanismo_denuncia: '',
    metodos_difusion: [],
    metodo_difusion_otro: '',
    tiene_ats: null,
    numero_ats: 0,
    acciones_ats: '',
    metodos_comunicacion: [],
    metodo_comunicacion_otro: '',
    // M14 Gap 7.7
    actividad_principal: '',
    principales_actividades: '',
    cedula_profesional: '',
    // NOM-030
    nom030_confirmado: false,
    nom030_evidencia_url: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Pre-fill policy when reaching step 3 OR when company data becomes available
  useEffect(() => {
    // Step 2 in array index = Step 3 visually (POLITICA)
    if (currentStep === 2 && formData.nombre_centro_trabajo && !loading) {
      // Generate policy texts with current form data
      const nombreEmpresa = formData.nombre_centro_trabajo || '[Nombre de la Empresa]';
      const representanteLegal = formData.nombre_representante_legal || '[Representante Legal]';
      
      // Get responsable name
      let responsableNom = '[Responsable NOM-035]';
      if (formData.responsable_tipo === 'externo') {
        responsableNom = formData.responsable_externo_nombre || '[Responsable NOM-035]';
      } else {
        const selectedUser = companyUsers.find(u => u.id === formData.responsable_user_id);
        if (selectedUser) {
          responsableNom = `${selectedUser.nombre}${selectedUser.apellido_paterno ? ' ' + selectedUser.apellido_paterno : ''}`;
        }
      }
      const cargoResponsable = formData.cargo_responsable || '';

      const policyTexts = {
        declaracion_principios: POLICY_TEMPLATES.declaracion_principios(nombreEmpresa, representanteLegal),
        compromisos_especificos: POLICY_TEMPLATES.compromisos_especificos(nombreEmpresa, responsableNom, cargoResponsable),
        mecanismo_denuncia: POLICY_TEMPLATES.mecanismo_denuncia(nombreEmpresa, responsableNom, cargoResponsable)
      };

      const updates = {};
      let updated = false;

      // Only update if empty or contains placeholder text
      if (!formData.declaracion_principios || formData.declaracion_principios.trim() === '' || formData.declaracion_principios.includes('[Nombre de la Empresa]')) {
        updates.declaracion_principios = policyTexts.declaracion_principios;
        updated = true;
      }
      if (!formData.compromisos_especificos || formData.compromisos_especificos.trim() === '' || formData.compromisos_especificos.includes('[Nombre de la Empresa]')) {
        updates.compromisos_especificos = policyTexts.compromisos_especificos;
        updated = true;
      }
      if (!formData.mecanismo_denuncia || formData.mecanismo_denuncia.trim() === '' || formData.mecanismo_denuncia.includes('[Nombre de la Empresa]')) {
        updates.mecanismo_denuncia = policyTexts.mecanismo_denuncia;
        updated = true;
      }

      if (updated) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [currentStep, formData.nombre_centro_trabajo, formData.nombre_representante_legal, loading, companyUsers]);

  const getResponsableName = () => {
    if (formData.responsable_tipo === 'externo') {
      return formData.responsable_externo_nombre || '[Responsable NOM-035]';
    } else {
      const selectedUser = companyUsers.find(u => u.id === formData.responsable_user_id);
      if (selectedUser) {
        return `${selectedUser.nombre}${selectedUser.apellido_paterno ? ' ' + selectedUser.apellido_paterno : ''}`;
      }
      return '[Responsable NOM-035]';
    }
  };

  const generatePolicyTexts = () => {
    const nombreEmpresa = formData.nombre_centro_trabajo || '[Nombre de la Empresa]';
    const representanteLegal = formData.nombre_representante_legal || '[Representante Legal]';
    const responsableNom = getResponsableName();
    const cargoResponsable = formData.cargo_responsable || '';

    return {
      declaracion_principios: POLICY_TEMPLATES.declaracion_principios(nombreEmpresa, representanteLegal),
      compromisos_especificos: POLICY_TEMPLATES.compromisos_especificos(nombreEmpresa, responsableNom, cargoResponsable),
      mecanismo_denuncia: POLICY_TEMPLATES.mecanismo_denuncia(nombreEmpresa, responsableNom, cargoResponsable)
    };
  };

  const loadData = async (calculateStep = true) => {
    if (!user?.empresa_id) return;
    setLoading(true);
    try {
      const [configRes, usersRes] = await Promise.all([
        axios.get(`/v5/nom035/${user.empresa_id}/config`),
        axios.get(`/companies/employees`)
      ]);

      if (configRes.data.success && configRes.data.data) {
        const cfg = configRes.data.data;
        setConfig(cfg);
        
        // Determine current step based on completed steps (only if calculateStep is true)
        const completedSteps = cfg.pasos_completados || {};
        let nextStep = 0;
        for (let i = 0; i < STEP_ORDER.length; i++) {
          if (!completedSteps[STEP_ORDER[i]]) {
            nextStep = i;
            break;
          }
          nextStep = i + 1;
        }
        
        if (calculateStep) {
          setCurrentStep(Math.min(nextStep, STEP_ORDER.length - 1));
        }

        // Pre-fill form data with ALL fields from all steps
        setFormData(prev => {
          const newFormData = {
            ...prev,
            // Paso 1: Datos empresa
            nombre_centro_trabajo: cfg.nombre_centro_trabajo || '',
            numero_trabajadores: cfg.numero_trabajadores || '',
            nombre_representante_legal: cfg.nombre_representante_legal || '',
            rfc_centro_trabajo: cfg.rfc_centro_trabajo || '',
            fecha_inicio_ciclo: cfg.fecha_inicio_ciclo ? cfg.fecha_inicio_ciclo.split('T')[0] : '',
            // Paso 2: Responsable
            responsable_tipo: cfg.responsable_tipo || (cfg.responsable_externo_nombre ? 'externo' : 'interno'),
            responsable_user_id: cfg.responsable_user_id || '',
            responsable_externo_nombre: cfg.responsable_externo_nombre || '',
            responsable_externo_email: cfg.responsable_externo_email || '',
            cargo_responsable: cfg.cargo_responsable || '',
            // Paso 3: Política - Keep empty if backend returns empty, will be pre-filled by useEffect
            declaracion_principios: cfg.declaracion_principios || '',
            compromisos_especificos: cfg.compromisos_especificos || '',
            mecanismo_denuncia: cfg.mecanismo_denuncia || '',
            // Paso 4: Difusión
            metodos_difusion: cfg.metodos_difusion || [],
            metodo_difusion_otro: cfg.metodo_difusion_otro || '',
            // Paso 5: ATS
            tiene_ats: cfg.tiene_ats,
            numero_ats: cfg.numero_ats || 0,
            acciones_ats: cfg.acciones_ats || '',
            // Paso 9: Comunicación
            metodos_comunicacion: cfg.metodos_comunicacion || [],
            metodo_comunicacion_otro: cfg.metodo_comunicacion_otro || '',
            // M14 Gap 7.7
            actividad_principal: cfg.actividad_principal || '',
            principales_actividades: cfg.principales_actividades || '',
            cedula_profesional: cfg.cedula_profesional || '',
            // NOM-030
            nom030_confirmado: cfg.nom030_confirmado || false,
            nom030_evidencia_url: cfg.nom030_evidencia_url || ''
          };
          
          // If we're on step 3 (POLITICA) and policy fields are empty, pre-fill them now
          if (nextStep === 2 && cfg.nombre_centro_trabajo && !cfg.declaracion_principios) {
            const nombreEmpresa = cfg.nombre_centro_trabajo;
            const representanteLegal = cfg.nombre_representante_legal || '[Representante Legal]';
            const responsableNom = cfg.responsable_externo_nombre || '[Responsable NOM-035]';
            const cargoResponsable = cfg.cargo_responsable || '';
            
            newFormData.declaracion_principios = POLICY_TEMPLATES.declaracion_principios(nombreEmpresa, representanteLegal);
            newFormData.compromisos_especificos = POLICY_TEMPLATES.compromisos_especificos(nombreEmpresa, responsableNom, cargoResponsable);
            newFormData.mecanismo_denuncia = POLICY_TEMPLATES.mecanismo_denuncia(nombreEmpresa, responsableNom, cargoResponsable);
          }
          
          return newFormData;
        });
      }

      if (usersRes.data?.data) {
        const sortedUsers = usersRes.data.data.sort((a, b) => {
          const nameA = `${a.nombre || ''} ${a.apellido_paterno || ''}`.toLowerCase();
          const nameB = `${b.nombre || ''} ${b.apellido_paterno || ''}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setCompanyUsers(sortedUsers);
      }
    } catch (error) {
      logger.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.empresa_id) return;
    setSaving(true);
    try {
      let endpoint = `/v5/nom035/${user.empresa_id}/config/step${currentStep + 1}`;
      let payload = {};

      switch (currentStep) {
        case 0: // DATOS_EMPRESA
          payload = {
            nombre_centro_trabajo: formData.nombre_centro_trabajo,
            numero_trabajadores: parseInt(formData.numero_trabajadores) || 0,
            nombre_representante_legal: formData.nombre_representante_legal,
            rfc_centro_trabajo: formData.rfc_centro_trabajo,
            fecha_inicio_ciclo: formData.fecha_inicio_ciclo,
            actividad_principal: formData.actividad_principal || null,
            principales_actividades: formData.principales_actividades || null,
            cedula_profesional: formData.cedula_profesional || null
          };
          break;
        case 1: // RESPONSABLE
          payload = {
            responsable_tipo: formData.responsable_tipo,
            responsable_user_id: formData.responsable_tipo === 'interno' ? formData.responsable_user_id : null,
            responsable_externo_nombre: formData.responsable_tipo === 'externo' ? formData.responsable_externo_nombre : null,
            responsable_externo_email: formData.responsable_tipo === 'externo' ? formData.responsable_externo_email : null,
            cargo_responsable: formData.cargo_responsable
          };
          break;
        case 2: // POLITICA
          payload = {
            declaracion_principios: formData.declaracion_principios,
            compromisos_especificos: formData.compromisos_especificos,
            mecanismo_denuncia: formData.mecanismo_denuncia
          };
          break;
        case 3: // DIFUSION_POLITICA
          payload = {
            metodos_difusion: formData.metodos_difusion,
            metodo_difusion_otro: formData.metodos_difusion.includes('otro') ? formData.metodo_difusion_otro : ''
          };
          break;
        case 4: // GUIA_I
          payload = {
            tiene_ats: formData.tiene_ats,
            numero_ats: formData.tiene_ats ? parseInt(formData.numero_ats) || 0 : 0,
            acciones_ats: formData.tiene_ats ? formData.acciones_ats : ''
          };
          break;
        case 5: // PARTICIPACION
          payload = { confirmado: true };
          break;
        case 6: // PROGRAMA
          payload = { confirmado: true };
          break;
        case 7: // EVIDENCIAS
          payload = { confirmado: true };
          break;
        case 8: // COMUNICACION
          payload = {
            metodos_comunicacion: formData.metodos_comunicacion,
            metodo_comunicacion_otro: formData.metodos_comunicacion.includes('otro') ? formData.metodo_comunicacion_otro : ''
          };
          break;
        default:
          payload = {};
      }

      const res = await axios.post(endpoint, payload);
      if (res.data.success) {
        toast.success(language === 'es' ? 'Guardado' : 'Saved');
        
        // Calculate next step before reloading data
        const nextStepIndex = currentStep < STEP_ORDER.length - 1 ? currentStep + 1 : currentStep;
        
        // Reload data (but don't let it change the step)
        await loadData(false); // Pass false to skip step calculation
        
        // Now advance to next step
        if (currentStep < STEP_ORDER.length - 1) {
          setCurrentStep(nextStepIndex);
        } else {
          toast.success(language === 'es' ? '¡Configuración completada!' : 'Configuration completed!');
        }
      }
    } catch (error) {
      // Handle Pydantic validation errors (array of objects) and regular errors
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Error';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation error - extract the message from first error
        errorMessage = errorDetail[0]?.msg || errorDetail[0]?.message || 'Validation error';
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const isStepCompleted = (stepKey) => config?.pasos_completados?.[stepKey] === true;
  const currentStepKey = STEP_ORDER[currentStep];
  const StepIcon = STEP_ICONS[currentStepKey] || ClipboardList;

  const toggleDiffusionMethod = (methodId) => {
    setFormData(prev => {
      const methods = prev.metodos_difusion.includes(methodId)
        ? prev.metodos_difusion.filter(m => m !== methodId)
        : [...prev.metodos_difusion, methodId];
      return { ...prev, metodos_difusion: methods };
    });
  };

  const toggleCommunicationMethod = (methodId) => {
    setFormData(prev => {
      const methods = prev.metodos_comunicacion.includes(methodId)
        ? prev.metodos_comunicacion.filter(m => m !== methodId)
        : [...prev.metodos_comunicacion, methodId];
      return { ...prev, metodos_comunicacion: methods };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-8 h-8 text-teal-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{texts.title}</h1>
          <p className="text-sm text-gray-500">{texts.subtitle}</p>
        </div>
      </div>

      {/* Progress bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {STEP_ORDER.map((stepKey, idx) => {
              const completed = isStepCompleted(stepKey);
              const isCurrent = idx === currentStep;
              const Icon = STEP_ICONS[stepKey] || ClipboardList;
              return (
                <React.Fragment key={stepKey}>
                  <button
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      completed 
                        ? 'bg-green-500 text-white' 
                        : isCurrent 
                          ? 'bg-teal-600 text-white ring-2 ring-teal-200' 
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    onClick={() => setCurrentStep(idx)}
                    title={texts.steps[stepKey]}
                  >
                    {completed ? <Check className="w-5 h-5" /> : Icon ? <Icon className="w-5 h-5" /> : null}
                  </button>
                  {idx < STEP_ORDER.length - 1 && (
                    <div className={`flex-shrink-0 w-8 h-1 rounded ${completed ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-4 text-center">
            <Badge variant="outline" className="text-teal-700">
              {texts.step} {currentStep + 1} {texts.of} 9: {texts.steps[currentStepKey]}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Step content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StepIcon className="w-5 h-5 text-teal-600" />
            {texts.steps[currentStepKey]}
          </CardTitle>
          {isStepCompleted(currentStepKey) && (
            <Badge className="w-fit bg-green-100 text-green-700">
              <Check className="w-3 h-3 mr-1" />
              {language === 'es' ? 'Completado' : 'Completed'}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Company Data */}
          {currentStep === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>{texts.workplaceName} *</Label>
                <Input 
                  value={formData.nombre_centro_trabajo} 
                  onChange={(e) => setFormData({ ...formData, nombre_centro_trabajo: e.target.value })} 
                />
              </div>
              <div>
                <Label>{texts.numberOfWorkers} *</Label>
                <Input 
                  type="number" 
                  value={formData.numero_trabajadores} 
                  onChange={(e) => setFormData({ ...formData, numero_trabajadores: e.target.value })} 
                />
              </div>
              <div>
                <Label>RFC *</Label>
                <Input 
                  value={formData.rfc_centro_trabajo} 
                  onChange={(e) => setFormData({ ...formData, rfc_centro_trabajo: e.target.value.toUpperCase() })} 
                  maxLength={13} 
                />
              </div>
              <div className="md:col-span-2">
                <Label>{texts.legalRepresentative} *</Label>
                <Input 
                  value={formData.nombre_representante_legal} 
                  onChange={(e) => setFormData({ ...formData, nombre_representante_legal: e.target.value })} 
                />
              </div>
              <div>
                <Label>{texts.cycleStartDate} *</Label>
                <Input 
                  type="date" 
                  value={formData.fecha_inicio_ciclo} 
                  onChange={(e) => setFormData({ ...formData, fecha_inicio_ciclo: e.target.value })} 
                />
              </div>
              <div className="md:col-span-2">
                <Label>{texts.mainActivity}</Label>
                <Input 
                  value={formData.actividad_principal} 
                  onChange={(e) => setFormData({ ...formData, actividad_principal: e.target.value })}
                  placeholder={language === 'es' ? 'Ej: Manufactura de autopartes' : 'E.g.: Auto parts manufacturing'}
                  data-testid="actividad-principal-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label>{texts.mainActivities}</Label>
                <textarea 
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]"
                  value={formData.principales_actividades} 
                  onChange={(e) => setFormData({ ...formData, principales_actividades: e.target.value })}
                  placeholder={language === 'es' ? 'Describe las principales actividades realizadas...' : 'Describe main activities performed...'}
                  data-testid="principales-actividades-input"
                />
              </div>
              <div>
                <Label>{texts.professionalLicense}</Label>
                <Input 
                  value={formData.cedula_profesional} 
                  onChange={(e) => setFormData({ ...formData, cedula_profesional: e.target.value })}
                  placeholder={language === 'es' ? 'Cédula profesional' : 'Professional license'}
                  data-testid="cedula-profesional-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Responsible */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-medium">{texts.responsibleType}</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.responsable_tipo === 'interno'}
                      onChange={() => setFormData({ ...formData, responsable_tipo: 'interno' })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span>{texts.internal}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.responsable_tipo === 'externo'}
                      onChange={() => setFormData({ ...formData, responsable_tipo: 'externo' })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span>{texts.external}</span>
                  </label>
                </div>
              </div>

              {formData.responsable_tipo === 'interno' ? (
                <div>
                  <Label>{texts.selectEmployee} *</Label>
                  <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between"
                      >
                        {formData.responsable_user_id
                          ? companyUsers.find(u => u.id === formData.responsable_user_id)
                            ? `${companyUsers.find(u => u.id === formData.responsable_user_id).nombre} ${companyUsers.find(u => u.id === formData.responsable_user_id).apellido_paterno || ''}`
                            : texts.selectEmployee
                          : texts.selectEmployee
                        }
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder={texts.searchEmployee} />
                        <CommandList>
                          <CommandEmpty>{texts.noEmployeeFound}</CommandEmpty>
                          <CommandGroup>
                            {companyUsers.map((user) => (
                              <CommandItem
                                key={user.id}
                                value={`${user.nombre} ${user.apellido_paterno || ''} ${user.email}`}
                                onSelect={() => {
                                  setFormData({ ...formData, responsable_user_id: user.id });
                                  setUserSearchOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${formData.responsable_user_id === user.id ? 'opacity-100' : 'opacity-0'}`}
                                />
                                <div>
                                  <p className="font-medium">{user.nombre} {user.apellido_paterno || ''}</p>
                                  <p className="text-xs text-gray-500">{user.email}</p>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{texts.externalName} *</Label>
                    <Input
                      value={formData.responsable_externo_nombre}
                      onChange={(e) => setFormData({ ...formData, responsable_externo_nombre: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{texts.externalEmail} *</Label>
                    <Input
                      type="email"
                      value={formData.responsable_externo_email}
                      onChange={(e) => setFormData({ ...formData, responsable_externo_email: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>{texts.position}</Label>
                <Input
                  value={formData.cargo_responsable}
                  onChange={(e) => setFormData({ ...formData, cargo_responsable: e.target.value })}
                  placeholder={language === 'es' ? 'Ej: Coordinador de RH' : 'E.g., HR Coordinator'}
                />
              </div>
            </div>
          )}

          {/* Step 3: Policy */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label>{texts.principlesDeclaration}</Label>
                <Textarea
                  value={formData.declaracion_principios}
                  onChange={(e) => setFormData({ ...formData, declaracion_principios: e.target.value })}
                  rows={4}
                />
              </div>
              <div>
                <Label>{texts.specificCommitments}</Label>
                <Textarea
                  value={formData.compromisos_especificos}
                  onChange={(e) => setFormData({ ...formData, compromisos_especificos: e.target.value })}
                  rows={6}
                />
              </div>
              <div>
                <Label>{texts.reportingMechanism}</Label>
                <Textarea
                  value={formData.mecanismo_denuncia}
                  onChange={(e) => setFormData({ ...formData, mecanismo_denuncia: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
          )}

          {/* Step 4: Diffusion */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <Label className="text-base">{texts.diffusionMethods}</Label>
              <div className="space-y-2">
                {DIFFUSION_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.metodos_difusion.includes(option.id)
                        ? 'bg-teal-50 border-teal-500'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleDiffusionMethod(option.id)}
                  >
                    <Checkbox checked={formData.metodos_difusion.includes(option.id)} />
                    <span>{language === 'es' ? option.label_es : option.label_en}</span>
                  </div>
                ))}
              </div>
              {formData.metodos_difusion.includes('otro') && (
                <div>
                  <Label>{texts.otherMethod}</Label>
                  <Input
                    value={formData.metodo_difusion_otro}
                    onChange={(e) => setFormData({ ...formData, metodo_difusion_otro: e.target.value })}
                    placeholder={texts.describeMethod}
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 5: Guide I (ATS) */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <Label className="text-base">{texts.atsQuestion}</Label>
              <div className="flex gap-4">
                <Button
                  variant={formData.tiene_ats === true ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, tiene_ats: true })}
                  className={formData.tiene_ats === true ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  {texts.yes}
                </Button>
                <Button
                  variant={formData.tiene_ats === false ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, tiene_ats: false })}
                  className={formData.tiene_ats === false ? 'bg-green-500 hover:bg-green-600' : ''}
                >
                  {texts.no}
                </Button>
              </div>
              {formData.tiene_ats && (
                <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div>
                    <Label>{texts.atsCount}</Label>
                    <Input
                      type="number"
                      value={formData.numero_ats}
                      onChange={(e) => setFormData({ ...formData, numero_ats: e.target.value })}
                      min={1}
                    />
                  </div>
                  <div>
                    <Label>{texts.atsActions}</Label>
                    <Textarea
                      value={formData.acciones_ats}
                      onChange={(e) => setFormData({ ...formData, acciones_ats: e.target.value })}
                      rows={3}
                      placeholder={language === 'es' ? 'Describe las acciones tomadas...' : 'Describe the actions taken...'}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Participation */}
          {currentStep === 5 && (
            <div className="text-center py-8">
              <ClipboardList className="w-16 h-16 mx-auto text-teal-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {language === 'es' ? 'Verificación de Participación' : 'Participation Verification'}
              </h3>
              <p className="text-gray-600 mb-4">
                {language === 'es'
                  ? 'Este paso verifica que la participación en las evaluaciones cumple con el mínimo requerido por la norma.'
                  : 'This step verifies that participation in evaluations meets the minimum required by the standard.'}
              </p>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? texts.saving : (language === 'es' ? 'Confirmar participación' : 'Confirm participation')}
              </Button>
            </div>
          )}

          {/* Step 7: Program */}
          {currentStep === 6 && (
            <div className="text-center py-8">
              <Target className="w-16 h-16 mx-auto text-teal-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {language === 'es' ? 'Programa de Atención' : 'Attention Program'}
              </h3>
              <p className="text-gray-600 mb-4">
                {language === 'es'
                  ? 'Documenta las acciones preventivas y correctivas derivadas del diagnóstico.'
                  : 'Document the preventive and corrective actions derived from the diagnosis.'}
              </p>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? texts.saving : (language === 'es' ? 'Confirmar programa' : 'Confirm program')}
              </Button>
            </div>
          )}

          {/* Step 8: Evidence */}
          {currentStep === 7 && (
            <div className="text-center py-8">
              <FileCheck className="w-16 h-16 mx-auto text-teal-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {language === 'es' ? 'Evidencias' : 'Evidence'}
              </h3>
              <p className="text-gray-600 mb-4">
                {language === 'es'
                  ? 'Gestiona las evidencias de cumplimiento de las acciones del programa.'
                  : 'Manage the compliance evidence for program actions.'}
              </p>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? texts.saving : (language === 'es' ? 'Confirmar evidencias' : 'Confirm evidence')}
              </Button>
            </div>
          )}

          {/* Step 9: Communication */}
          {currentStep === 8 && (
            <div className="space-y-4">
              <Label className="text-base">{texts.communicationMethods}</Label>
              <p className="text-sm text-gray-500">
                {language === 'es'
                  ? 'Selecciona los métodos que utilizarás para comunicar los resultados del diagnóstico y las acciones del programa a los trabajadores.'
                  : 'Select the methods you will use to communicate the diagnosis results and program actions to workers.'}
              </p>
              <div className="space-y-2">
                {COMMUNICATION_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.metodos_comunicacion.includes(option.id)
                        ? 'bg-teal-50 border-teal-500'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => toggleCommunicationMethod(option.id)}
                  >
                    <Checkbox checked={formData.metodos_comunicacion.includes(option.id)} />
                    <span>{language === 'es' ? option.label_es : option.label_en}</span>
                  </div>
                ))}
              </div>
              {formData.metodos_comunicacion.includes('otro') && (
                <div>
                  <Label>{texts.otherMethod}</Label>
                  <Input
                    value={formData.metodo_comunicacion_otro}
                    onChange={(e) => setFormData({ ...formData, metodo_comunicacion_otro: e.target.value })}
                    placeholder={texts.describeCommunication}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {texts.back}
        </Button>
        
        <div className="flex gap-2">
          {currentStep < 5 && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {texts.saving}
                </>
              ) : (
                <>
                  {texts.save}
                  {currentStep < STEP_ORDER.length - 1 && <ArrowRight className="w-4 h-4 ml-2" />}
                </>
              )}
            </Button>
          )}
          {currentStep === 8 && (
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {texts.saving}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {texts.complete}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* NOM-030 Integration (optional) */}
      <Card className="border-amber-200 bg-amber-50/50" data-testid="nom030-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            {texts.nom030Label}
          </CardTitle>
          <CardDescription className="text-xs">
            {language === 'es' 
              ? 'Paso opcional: Confirma la verificación de la relación con NOM-030-STPS (Servicios preventivos de seguridad y salud en el trabajo)'
              : 'Optional step: Confirm NOM-030-STPS relationship verification (Preventive safety and health services)'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div 
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
              formData.nom030_confirmado ? 'bg-amber-100 border-amber-400' : 'bg-white border-gray-200'
            }`}
            onClick={() => setFormData({ ...formData, nom030_confirmado: !formData.nom030_confirmado })}
          >
            <Checkbox checked={formData.nom030_confirmado} data-testid="nom030-checkbox" />
            <span className="text-sm">{texts.nom030Check}</span>
          </div>
          {formData.nom030_confirmado && (
            <div>
              <Label className="text-sm">{texts.nom030Evidence}</Label>
              <Input 
                value={formData.nom030_evidencia_url}
                onChange={(e) => setFormData({ ...formData, nom030_evidencia_url: e.target.value })}
                placeholder="https://..."
                className="mt-1"
                data-testid="nom030-evidence-input"
              />
            </div>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              try {
                setSaving(true);
                await axios.post(`/v5/nom035/${user.empresa_id}/config/nom030`, {
                  nom030_confirmado: formData.nom030_confirmado,
                  nom030_evidencia_url: formData.nom030_evidencia_url || null
                });
                toast.success(language === 'es' ? 'NOM-030 guardado' : 'NOM-030 saved');
              } catch (err) {
                toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
            data-testid="nom030-save-btn"
          >
            {saving ? texts.saving : texts.save}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default NOM035ConfigPage;
